import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '../../layout/main-layout';
import DuoPeriodPicker from '../../components/ui/duo-period-picker';
import DuoTablePagination from '../../components/ui/duo-table-pagination';
import { APP_API_URL } from '../../utils';
import formatNumber, {
  defaultFilterDates,
  DUO_PERIOD_OPTIONS,
  formatDateWithTime,
} from '../../utils/functions';

const PERIOD_ALL = { label: 'Tout', value: '' };
const PERIOD_TODAY =
  DUO_PERIOD_OPTIONS.find((p) => p.value === "aujourd'hui") ?? { label: "Aujourd'hui", value: "aujourd'hui" };
const periodOptionsWithAll = [PERIOD_ALL, ...DUO_PERIOD_OPTIONS];

const DEFAULT_FILTERS = {
  search: '',
  period: '',
  ...defaultFilterDates(),
  statut: '',
};

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const statusBadgeClass = (statut) => {
  if (statut === 'approuvé') return 'duo-badge-pill duo-badge-pill--info';
  if (statut === 'en cours') return 'duo-badge-pill duo-badge-pill--warn';
  if (statut === 'livré') return 'duo-badge-pill duo-badge-pill--ok';
  if (statut === 'annulé') return 'duo-badge-pill duo-badge-pill--off';
  return 'duo-badge-pill';
};

const statusLabel = (statut) => {
  if (statut === 'approuvé') return 'Approuvé';
  if (statut === 'en cours') return 'En cours';
  if (statut === 'livré') return 'Livré';
  if (statut === 'annulé') return 'Annulé';
  return statut ?? '—';
};

export default function CustomerInvoices() {
  const { customerId } = useParams();

  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({ total_montant_net: 0, total_commandes: 0 });
  const [meta, setMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_ALL);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(() => ({ ...defaultFilterDates(), statut: '' }));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/customers/${customerId}/commandes`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          search: filters.search || undefined,
          period: filters.period || undefined,
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined,
          statut_commande: filters.statut || undefined,
        },
      });

      setCustomer(response.data.customer ?? null);
      setOrders(response.data.data ?? []);
      setSummary(response.data.summary ?? { total_montant_net: 0, total_commandes: 0 });
      setMeta(response.data.meta ?? null);
      setLastPage(response.data.meta?.last_page ?? 1);
    } catch (error) {
      console.error('Erreur chargement factures client :', error);
      setOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, [customerId, currentPage, sortConfig, filters]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handlePeriodSelect = (option) => {
    setSelectedPeriod(option);
    setDraftFilters({ dateFrom: '', dateTo: '', statut: draftFilters.statut });
    setFilters((prev) => ({
      ...prev,
      period: option.value,
      dateFrom: '',
      dateTo: '',
    }));
    setCurrentPage(1);
  };

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setSelectedPeriod(PERIOD_ALL);
    setFilters((prev) => ({
      ...prev,
      period: '',
      dateFrom: draftFilters.dateFrom,
      dateTo: draftFilters.dateTo,
      statut: draftFilters.statut,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setSelectedPeriod(PERIOD_ALL);
    setDraftFilters({ ...defaultFilterDates(), statut: '' });
    setFilters({ ...DEFAULT_FILTERS });
    setCurrentPage(1);
  };

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < lastPage) {
      setCurrentPage((p) => p + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage((p) => p - 1);
    }
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">
            <Link to="/clients" className="text-decoration-none">
              Clients
            </Link>
            {' / Factures'}
          </p>
          <h1 className="duo-hero-title">{customer?.name ?? 'Client'}</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            {customer?.client_code && <>Code {customer.client_code} · </>}
            {customer?.numero && <>Tél. {customer.numero} · </>}
            Historique des commandes et factures
          </p>
        </div>

        <div className="duo-kpi-row mb-3">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Commandes (filtre)</p>
            <p className="duo-kpi-chip__value">{summary.total_commandes ?? 0}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Total TTC (filtre)</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
              {formatNumber(summary.total_montant_net ?? 0)} FCFA
            </p>
          </div>
        </div>

        <div className="mb-3">
          <DuoPeriodPicker
            options={periodOptionsWithAll}
            selectedOption={selectedPeriod}
            onSelect={handlePeriodSelect}
          />
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Factures / commandes
            </span>
            <div className="duo-toolbar__actions">
              <button
                type="button"
                title="Rafraîchir"
                onClick={fetchOrders}
                className="duo-btn duo-btn--ghost duo-btn--icon"
              >
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              <Link to="/clients" className="duo-btn duo-btn--ghost">
                <i className="bi bi-arrow-left" /> Retour
              </Link>
            </div>
          </div>

          {filtersOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <input
                    type="date"
                    className="duo-input"
                    value={draftFilters.dateFrom}
                    disabled={!!filters.period}
                    onChange={(e) => setDraftFilters((p) => ({ ...p, dateFrom: e.target.value }))}
                  />
                  <input
                    type="date"
                    className="duo-input"
                    value={draftFilters.dateTo}
                    disabled={!!filters.period}
                    onChange={(e) => setDraftFilters((p) => ({ ...p, dateTo: e.target.value }))}
                  />
                  <select
                    className="duo-select"
                    value={draftFilters.statut}
                    onChange={(e) => setDraftFilters((p) => ({ ...p, statut: e.target.value }))}
                  >
                    <option value="">Tous les statuts</option>
                    <option value="approuvé">Approuvé</option>
                    <option value="en cours">En cours</option>
                    <option value="livré">Livré</option>
                    <option value="annulé">Annulé</option>
                  </select>
                </div>
                <div className="d-flex justify-content-end gap-2 flex-wrap mt-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    Appliquer
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher référence, statut..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {(filters.period || filters.dateFrom || filters.dateTo || filters.statut) && (
            <div className="px-3 py-2 small text-muted border-bottom" style={{ borderColor: '#f0f0f0' }}>
              Filtres actifs :
              {filters.period && (
                <span className="ms-2 duo-badge-pill duo-badge-pill--info">
                  {periodOptionsWithAll.find((p) => p.value === filters.period)?.label ?? filters.period}
                </span>
              )}
              {(filters.dateFrom || filters.dateTo) && (
                <span className="ms-2">
                  Plage {filters.dateFrom || '…'} → {filters.dateTo || '…'}
                </span>
              )}
              {filters.statut && (
                <span className="ms-2 duo-badge-pill">{statusLabel(filters.statut)}</span>
              )}
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('designation')}>
                    Référence <SortIcon active={isSortActive('designation')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('created_at')}>
                    Date <SortIcon active={isSortActive('created_at')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('statut_commande')}>
                    Statut <SortIcon active={isSortActive('statut_commande')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('montant_net')}>
                    Montant TTC <SortIcon active={isSortActive('montant_net')} direction={sortConfig.direction} />
                  </th>
                  <th>Facture</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map((order) => (
                    <tr key={order.id}>
                      <td className="fw-bold">{order.designation}</td>
                      <td className="text-nowrap">{formatDateWithTime(order.date_creation)}</td>
                      <td>
                        <span className={statusBadgeClass(order.statut_commande)}>
                          {statusLabel(order.statut_commande)}
                        </span>
                      </td>
                      <td className="text-nowrap">{formatNumber(order.montant_net)} FCFA</td>
                      <td>
                        {order.has_emecef ? (
                          <span className="duo-badge-pill duo-badge-pill--ok">Normalisée</span>
                        ) : (
                          <span className="duo-badge-pill duo-badge-pill--off">—</span>
                        )}
                      </td>
                      <td>
                        <a
                          href={`/commande/${order.designation}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="duo-btn duo-btn--primary duo-btn--sm"
                          title="Ouvrir le détail de la commande dans un nouvel onglet"
                        >
                          <i className="bi bi-box-arrow-up-right me-1" />
                          Voir détail
                        </a>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="duo-empty-row">
                      Aucune commande pour ce client sur la période sélectionnée.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <DuoTablePagination
            currentPage={currentPage}
            totalPages={lastPage}
            totalItems={meta?.total ?? 0}
            pageSize={meta?.per_page ?? 10}
            onPageChange={handlePageChange}
            itemLabel="commande"
          />
        </section>
      </div>
    </MainLayout>
  );
}
